import type {
  EvidenceSpan,
  ProductionPlanView,
  ProductionRequirementView,
} from '@scoutaround/script-breakdown-shared';

import { Callout } from '../Callout.js';
import { EVIDENCE_STATUS_LABELS } from './labels.js';
import { usePlan } from './context.js';
import '../../styles/production.css';

/**
 * AN INCOMPLETE ANALYSIS MUST NEVER READ AS A COMPLETE PLAN.
 *
 * No production reading for this draft, one that finished in part, failed or is
 * unavailable, or scenes it never read: the plan on screen is then a part of the
 * production's needs, and every plan view says so above anything else.
 */
export function isIncomplete(plan: ProductionPlanView): boolean {
  return plan.analysis === null || plan.analysis.status !== 'COMPLETED' || plan.analysis.scenesUnread > 0;
}

export function incompleteReasons(plan: ProductionPlanView): string[] {
  const analysis = plan.analysis;
  if (analysis === null) return ['The production reading has not run on this draft yet.'];

  const reasons: string[] = [];
  if (analysis.status === 'PARTIAL') reasons.push('The production reading finished only in part.');
  if (analysis.status === 'FAILED') reasons.push('The production reading failed.');
  if (analysis.status === 'UNAVAILABLE') reasons.push('The production reading is not available for this draft.');
  if (analysis.scenesUnread > 0) {
    reasons.push(
      `${String(analysis.scenesUnread)} ${analysis.scenesUnread === 1 ? 'scene was' : 'scenes were'} not read, so what they need is missing.`,
    );
  }

  return reasons;
}

export function IncompleteBanner({ plan }: { plan: ProductionPlanView }) {
  if (!isIncomplete(plan)) return null;

  return (
    <Callout tone="warn" title="Incomplete analysis: this is not the whole plan" testId="production-plan-incomplete">
      <ul className="pp-plain-list">
        {incompleteReasons(plan).map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
    </Callout>
  );
}

/** The plan is loading, or could not be read — said once, in place of the view. */
export function PlanLoadState({ testId }: { testId: string }) {
  const { loading, error } = usePlan();

  if (loading) {
    return (
      <p className="mm pp-note" data-testid={`${testId}-loading`}>
        Loading the production plan…
      </p>
    );
  }

  return (
    <p className="pp-error" role="alert" data-testid={`${testId}-error`}>
      {error ?? 'The production plan could not be loaded.'}
    </p>
  );
}

/**
 * Quotes from the screenplay, as quotes, each with the scene it is from.
 *
 * A quote FROM THE SCENE IN VIEW (`sceneId`) offers "Show in script" while the plan context carries
 * the reader's `showEvidence`: the reader then finds the quote in the text it shows and pulses it.
 * A quote from another scene, or any quote where no reader is beside the plan, stays text — the
 * button would otherwise leave the scene the producer is deciding on, or do nothing.
 */
export function EvidenceQuotes({
  spans,
  testId,
  sceneId,
}: {
  spans: readonly EvidenceSpan[];
  testId?: string;
  /** The scene the view is about; only its quotes can be shown in the reader. */
  sceneId?: string;
}) {
  const { sceneLabel, showEvidence } = usePlan();

  if (spans.length === 0) return <p className="mm pp-note">No quote from the screenplay.</p>;

  return (
    <ul className="pp-quotes" data-testid={testId}>
      {spans.map((span, index) => {
        const note = EVIDENCE_STATUS_LABELS[span.status];
        const show = showEvidence !== undefined && sceneId !== undefined && span.sceneId === sceneId ? showEvidence : null;

        return (
          <li key={`${span.sceneId}-${String(span.start ?? index)}-${String(index)}`}>
            <q>{span.text}</q>
            <span className="mm">
              {` — ${sceneLabel(span.sceneId)}`}
              {note === null ? '' : ` · ${note}`}
            </span>
            {show === null ? null : (
              <button
                type="button"
                className="pp-quote-show"
                data-testid={`${testId ?? 'production-quotes'}-show-${String(index)}`}
                onClick={() => {
                  show(span);
                }}
              >
                Show in script
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** The requirements a scene carries: those whose scenes include it. */
export function sceneRequirements(plan: ProductionPlanView, sceneId: string): ProductionRequirementView[] {
  return plan.requirements.filter((r) => r.scenes.some((s) => s.sceneId === sceneId));
}

/** The date a person decided something, as the workspace shows dates. */
export function shortDate(iso: string | null): string | null {
  if (iso === null) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
